import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Separator } from '../components/ui/separator';
import { Alert, AlertDescription, AlertTitle } from '../components/ui/alert';
import { ScrollArea } from '../components/ui/scroll-area';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';
import { 
  FileText, 
  CheckCircle2, 
  AlertCircle, 
  Loader2, 
  Eye, 
  Sparkles,
  Check,
  X,
  Bot,
  ArrowRight,
  AlertTriangle,
  Plus,
  Equal,
  CreditCard,
  User,
  MapPin,
  IdCard
} from 'lucide-react';
import api from '../lib/api';

// Document type icons and labels
const DOCUMENT_TYPES = {
  CARTA_IDENTITA: { label: 'Carta d\'Identità', icon: IdCard, color: 'bg-blue-500' },
  PATENTE: { label: 'Patente di Guida', icon: CreditCard, color: 'bg-green-500' },
  PASSAPORTO: { label: 'Passaporto', icon: FileText, color: 'bg-purple-500' },
  TESSERA_SANITARIA: { label: 'Tessera Sanitaria', icon: CreditCard, color: 'bg-red-500' },
  CODICE_FISCALE: { label: 'Codice Fiscale', icon: CreditCard, color: 'bg-amber-500' },
  PERMESSO_SOGGIORNO: { label: 'Permesso di Soggiorno', icon: FileText, color: 'bg-teal-500' },
  ALTRO: { label: 'Altro Documento', icon: FileText, color: 'bg-gray-500' }
};

// ID Document field labels
const ID_FIELD_LABELS = {
  documento_tipo: "Tipo Documento",
  documento_numero: "Numero Documento",
  documento_ente_rilascio: "Ente Rilascio",
  documento_data_rilascio: "Data Rilascio",
  documento_scadenza: "Data Scadenza",
  documento_luogo_rilascio: "Luogo Rilascio",
  cognome: "Cognome",
  nome: "Nome",
  data_nascita: "Data di Nascita",
  luogo_nascita: "Luogo di Nascita",
  provincia_nascita: "Provincia Nascita",
  sesso: "Sesso",
  codice_fiscale: "Codice Fiscale",
  cittadinanza: "Cittadinanza",
  stato_civile: "Stato Civile",
  indirizzo: "Indirizzo",
  comune: "Comune",
  provincia: "Provincia",
  cap: "CAP",
  altezza: "Altezza (cm)",
  occhi: "Colore Occhi",
  capelli: "Colore Capelli",
  segni_particolari: "Segni Particolari",
  patente_categorie: "Categorie Patente",
  patente_data_prima: "Data Prima Patente",
  patente_punti: "Punti Patente",
  tessera_numero: "Numero Tessera Sanitaria",
  tessera_scadenza: "Scadenza Tessera",
  asl_appartenenza: "ASL di Appartenenza",
  medico_base: "Medico di Base",
};

// Sections for ID documents
const ID_SECTIONS = [
  {
    key: 'documento',
    title: 'Dati Documento',
    icon: IdCard,
    fields: ['documento_tipo', 'documento_numero', 'documento_ente_rilascio', 
             'documento_data_rilascio', 'documento_scadenza', 'documento_luogo_rilascio']
  },
  {
    key: 'anagrafica',
    title: 'Anagrafica',
    icon: User,
    fields: ['cognome', 'nome', 'data_nascita', 'luogo_nascita', 'provincia_nascita',
             'sesso', 'codice_fiscale', 'cittadinanza', 'stato_civile']
  },
  {
    key: 'residenza',
    title: 'Residenza',
    icon: MapPin,
    fields: ['indirizzo', 'comune', 'provincia', 'cap']
  }
];

/**
 * DocumentTypeBadge - Shows detected document type
 */
export const DocumentTypeBadge = ({ type }) => {
  const config = DOCUMENT_TYPES[type] || DOCUMENT_TYPES.ALTRO;
  const Icon = config.icon;
  
  return (
    <Badge className={`${config.color} text-white flex items-center gap-1.5 px-3 py-1`}>
      <Icon className="h-4 w-4" />
      {config.label}
    </Badge>
  );
};

/**
 * ComparisonField - Shows extracted vs existing value with selection
 */
const ComparisonField = ({ field, onSelectionChange }) => {
  const { 
    field: fieldKey, 
    label, 
    extracted, 
    existing, 
    status, 
    use_extracted 
  } = field;
  
  const [selected, setSelected] = useState(use_extracted);
  
  const handleToggle = () => {
    const newValue = !selected;
    setSelected(newValue);
    onSelectionChange(fieldKey, newValue);
  };
  
  // Color coding based on status
  const getStatusColor = () => {
    switch (status) {
      case 'matching': return 'border-green-200 bg-green-50';
      case 'conflict': return 'border-amber-200 bg-amber-50';
      case 'new': return 'border-blue-200 bg-blue-50';
      default: return 'border-gray-200';
    }
  };
  
  const getStatusIcon = () => {
    switch (status) {
      case 'matching': return <Equal className="h-4 w-4 text-green-600" />;
      case 'conflict': return <AlertTriangle className="h-4 w-4 text-amber-600" />;
      case 'new': return <Plus className="h-4 w-4 text-blue-600" />;
      default: return null;
    }
  };
  
  return (
    <div className={`p-3 rounded-lg border ${getStatusColor()}`}>
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          {getStatusIcon()}
          <span className="font-medium text-sm">{label}</span>
        </div>
        {status !== 'matching' && (
          <Checkbox 
            checked={selected}
            onCheckedChange={handleToggle}
            className="data-[state=checked]:bg-primary"
          />
        )}
      </div>
      
      <div className="grid grid-cols-2 gap-3 text-sm">
        {/* Extracted Value */}
        <div className={`p-2 rounded ${selected && status !== 'matching' ? 'bg-primary/10 ring-2 ring-primary' : 'bg-white'}`}>
          <div className="text-xs text-gray-500 mb-1 flex items-center gap-1">
            <Sparkles className="h-3 w-3" />
            Estratto
          </div>
          <div className={`font-medium ${!extracted ? 'text-gray-400 italic' : ''}`}>
            {extracted || '-'}
          </div>
        </div>
        
        {/* Existing Value */}
        <div className={`p-2 rounded ${!selected && status !== 'matching' ? 'bg-gray-100 ring-2 ring-gray-400' : 'bg-white'}`}>
          <div className="text-xs text-gray-500 mb-1 flex items-center gap-1">
            <User className="h-3 w-3" />
            Esistente
          </div>
          <div className={`font-medium ${!existing ? 'text-gray-400 italic' : ''}`}>
            {existing || '-'}
          </div>
        </div>
      </div>
    </div>
  );
};

/**
 * IdDocumentVerificationDialog - Dialog for verifying ID document extraction with merge
 */
export const IdDocumentVerificationDialog = ({ 
  isOpen, 
  onClose, 
  extraction, 
  onValidate,
  documentPreviewUrl 
}) => {
  const [fieldSelections, setFieldSelections] = useState({});
  const [isValidating, setIsValidating] = useState(false);
  const [activeTab, setActiveTab] = useState('documento');
  
  // Initialize selections from comparison
  useEffect(() => {
    if (extraction?.customer_comparison?.fields) {
      const initialSelections = {};
      extraction.customer_comparison.fields.forEach(field => {
        if (field.status !== 'matching') {
          initialSelections[field.field] = field.use_extracted;
        }
      });
      setFieldSelections(initialSelections);
    }
  }, [extraction]);
  
  const handleSelectionChange = (fieldKey, useExtracted) => {
    setFieldSelections(prev => ({
      ...prev,
      [fieldKey]: useExtracted
    }));
  };
  
  const handleValidate = async () => {
    setIsValidating(true);
    try {
      await onValidate({
        extraction_id: extraction.id,
        field_selections: fieldSelections,
        apply_to_customer: true
      });
      toast.success('Dati validati e merge completato');
      onClose();
    } catch (error) {
      console.error('Validation error:', error);
      toast.error('Errore durante la validazione');
    } finally {
      setIsValidating(false);
    }
  };
  
  if (!extraction) return null;
  
  const comparison = extraction.customer_comparison;
  const flattened = extraction.flattened_data || {};
  const docType = extraction.document_type_detected || flattened.documento_tipo || 'ALTRO';
  
  // Count fields by status
  const summary = comparison?.summary || { matching: 0, conflicts: 0, new_in_extraction: 0 };
  const selectedCount = Object.values(fieldSelections).filter(v => v).length;
  
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-5xl max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-3">
            <Bot className="h-5 w-5 text-primary" />
            Verifica Documento d'Identità
            <DocumentTypeBadge type={docType} />
          </DialogTitle>
          <DialogDescription>
            Confronta i dati estratti con quelli esistenti e scegli quali aggiornare
          </DialogDescription>
        </DialogHeader>
        
        {/* Summary Badges */}
        <div className="flex items-center gap-3 py-2">
          <Badge variant="outline" className="bg-green-50 text-green-700 border-green-300">
            <Equal className="h-3 w-3 mr-1" />
            {summary.matching} corrispondenti
          </Badge>
          {summary.conflicts > 0 && (
            <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-300">
              <AlertTriangle className="h-3 w-3 mr-1" />
              {summary.conflicts} conflitti
            </Badge>
          )}
          {summary.new_in_extraction > 0 && (
            <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-300">
              <Plus className="h-3 w-3 mr-1" />
              {summary.new_in_extraction} nuovi
            </Badge>
          )}
          {selectedCount > 0 && (
            <Badge className="bg-primary text-white">
              <Check className="h-3 w-3 mr-1" />
              {selectedCount} da applicare
            </Badge>
          )}
        </div>
        
        <div className="flex-1 overflow-hidden">
          <div className="grid grid-cols-12 gap-4 h-full">
            {/* Left side - Document Preview */}
            <div className="col-span-4 border rounded-lg overflow-hidden bg-gray-50">
              <div className="p-3 bg-gray-100 border-b flex items-center justify-between">
                <span className="text-sm font-medium">Documento Originale</span>
                {documentPreviewUrl && (
                  <Button variant="ghost" size="sm" asChild>
                    <a href={documentPreviewUrl} target="_blank" rel="noopener noreferrer">
                      <Eye className="h-4 w-4 mr-1" />
                      Apri
                    </a>
                  </Button>
                )}
              </div>
              {documentPreviewUrl ? (
                <iframe 
                  src={documentPreviewUrl} 
                  className="w-full h-[500px]"
                  title="Document Preview"
                />
              ) : (
                <div className="flex items-center justify-center h-[500px] text-gray-400">
                  <IdCard className="h-12 w-12" />
                </div>
              )}
            </div>
            
            {/* Right side - Comparison Fields */}
            <div className="col-span-8 flex flex-col overflow-hidden">
              {comparison ? (
                <>
                  {/* Tabs for sections */}
                  <Tabs value={activeTab} onValueChange={setActiveTab} className="flex-1 flex flex-col overflow-hidden">
                    <TabsList className="grid grid-cols-3 w-full">
                      {ID_SECTIONS.map(section => {
                        const Icon = section.icon;
                        return (
                          <TabsTrigger key={section.key} value={section.key} className="text-xs">
                            <Icon className="h-4 w-4 mr-1" />
                            {section.title}
                          </TabsTrigger>
                        );
                      })}
                    </TabsList>
                    
                    <ScrollArea className="flex-1 mt-4">
                      {ID_SECTIONS.map(section => {
                        const sectionFields = comparison.fields.filter(f => section.fields.includes(f.field));
                        return (
                        <TabsContent key={section.key} value={section.key} className="m-0">
                          <Card>
                            <CardHeader className="py-3">
                              <CardTitle className="text-sm flex items-center gap-2">
                                {React.createElement(section.icon, { className: "h-4 w-4" })}
                                {section.title}
                              </CardTitle>
                            </CardHeader>
                            <CardContent className="space-y-3">
                              {sectionFields.map(field => (
                                  <ComparisonField
                                    key={field.field}
                                    field={field}
                                    onSelectionChange={handleSelectionChange}
                                  />
                              ))}
                              {sectionFields.length === 0 && (
                                <p className="text-sm text-gray-500 text-center py-4">
                                  Nessun dato estratto per questa sezione
                                </p>
                              )}
                            </CardContent>
                          </Card>
                        </TabsContent>
                        );
                      })}
                    </ScrollArea>
                  </Tabs>
                </>
              ) : (
                // No comparison - show simple extracted data view
                <ScrollArea className="flex-1">
                  <Card>
                    <CardHeader className="py-3">
                      <CardTitle className="text-sm">Dati Estratti</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="grid grid-cols-2 gap-4">
                        {Object.entries(flattened).map(([key, value]) => (
                          <div key={key} className="space-y-1">
                            <Label className="text-xs text-gray-500">
                              {ID_FIELD_LABELS[key] || key}
                            </Label>
                            <div className="font-medium text-sm">
                              {value || '-'}
                            </div>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                </ScrollArea>
              )}
            </div>
          </div>
        </div>
        
        <Separator className="my-4" />
        
        {/* Footer actions */}
        <DialogFooter className="flex items-center justify-between">
          <div className="text-sm text-gray-600">
            {selectedCount > 0 ? (
              <span className="flex items-center gap-2">
                <CheckCircle2 className="h-4 w-4 text-primary" />
                {selectedCount} campi saranno aggiornati nel cliente
              </span>
            ) : (
              <span className="flex items-center gap-2">
                <AlertCircle className="h-4 w-4 text-gray-400" />
                Seleziona i campi da aggiornare
              </span>
            )}
          </div>
          
          <div className="flex gap-2">
            <Button variant="outline" onClick={onClose}>
              Annulla
            </Button>
            <Button onClick={handleValidate} disabled={isValidating}>
              {isValidating ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Validazione...
                </>
              ) : (
                <>
                  <Check className="h-4 w-4 mr-2" />
                  Conferma Merge
                </>
              )}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

/**
 * SimpleIdDataView - Simple view of extracted ID data (when no customer exists)
 */
export const SimpleIdDataView = ({ extraction, onValidate }) => {
  const flattened = extraction?.flattened_data || {};
  const docType = extraction?.document_type_detected || 'ALTRO';
  const [isValidating, setIsValidating] = useState(false);
  
  const handleApply = async () => {
    setIsValidating(true);
    try {
      await onValidate({
        extraction_id: extraction.id,
        validated_data: flattened,
        apply_to_customer: true,
        apply_to_contract: true
      });
      toast.success('Dati applicati con successo');
    } catch (error) {
      toast.error('Errore durante l\'applicazione');
    } finally {
      setIsValidating(false);
    }
  };
  
  return (
    <Card className="border-2 border-primary/20">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="text-sm flex items-center gap-2">
            <Bot className="h-4 w-4 text-primary" />
            Dati Estratti
          </CardTitle>
          <DocumentTypeBadge type={docType} />
        </div>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-2 gap-3 mb-4">
          {Object.entries(flattened).slice(0, 8).map(([key, value]) => (
            <div key={key} className="text-sm">
              <span className="text-gray-500">{ID_FIELD_LABELS[key] || key}: </span>
              <span className="font-medium">{value}</span>
            </div>
          ))}
        </div>
        
        <Button onClick={handleApply} disabled={isValidating} className="w-full">
          {isValidating ? (
            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
          ) : (
            <Check className="h-4 w-4 mr-2" />
          )}
          Applica al Cliente
        </Button>
      </CardContent>
    </Card>
  );
};

export default IdDocumentVerificationDialog;
